import { useState, useEffect } from "react";
import axios from "axios";
import { 
  Plus, Edit, Trash2, Globe, ExternalLink, Calendar, Code, 
  Eye, EyeOff, Star, Upload, X, Loader2
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` } });

const PROJECT_TYPES = [
  { value: "e-commerce", label: "E-Commerce" },
  { value: "corporate", label: "Corporate Website" },
  { value: "saas", label: "SaaS Application" },
  { value: "portfolio", label: "Portfolio" },
  { value: "landing-page", label: "Landing Page" },
  { value: "custom", label: "Custom Application" }
];

export default function AdminWebsiteProjects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const [form, setForm] = useState({
    title: "",
    client_name: "",
    description: "",
    project_type: "corporate",
    project_url: "",
    technologies: "",
    completion_date: "",
    is_featured: true,
    is_visible: true
  });

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API}/admin/website-projects`, authHeader());
      setProjects(res.data);
    } catch (err) {
      console.error("Failed to fetch projects:", err);
      if (err.response?.status === 401) {
        localStorage.removeItem("admin_token");
        window.location.href = "/admin/login";
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const resetForm = () => {
    setForm({
      title: "",
      client_name: "",
      description: "",
      project_type: "corporate",
      project_url: "",
      technologies: "",
      completion_date: "",
      is_featured: true,
      is_visible: true
    });
    setEditingProject(null);
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (project) => {
    setForm({
      title: project.title || "",
      client_name: project.client_name || "",
      description: project.description || "",
      project_type: project.project_type || "corporate",
      project_url: project.project_url || "",
      technologies: project.technologies || "",
      completion_date: project.completion_date || "",
      is_featured: project.is_featured ?? true,
      is_visible: project.is_visible ?? true
    });
    setEditingProject(project);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingProject) {
        await axios.put(`${API}/admin/website-projects/${editingProject.id}`, form, authHeader());
      } else {
        await axios.post(`${API}/admin/website-projects`, form, authHeader());
      }
      setShowModal(false);
      resetForm();
      fetchProjects();
    } catch (err) {
      console.error("Failed to save project:", err);
      alert(err.response?.data?.detail || "Failed to save project");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (project) => {
    if (!window.confirm(`Delete project "${project.title}"?`)) return;
    
    try {
      await axios.delete(`${API}/admin/website-projects/${project.id}`, authHeader());
      fetchProjects();
    } catch (err) {
      console.error("Failed to delete project:", err);
      alert("Failed to delete project");
    }
  };

  const handleScreenshotUpload = async (projectId, file) => {
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      await axios.post(`${API}/admin/website-projects/${projectId}/screenshot`, formData, {
        ...authHeader(),
        headers: {
          ...authHeader().headers,
          "Content-Type": "multipart/form-data"
        }
      });
      fetchProjects();
    } catch (err) {
      console.error("Failed to upload screenshot:", err);
      alert("Failed to upload screenshot");
    } finally {
      setUploading(false);
    }
  };

  const getProjectTypeLabel = (type) => {
    return PROJECT_TYPES.find(t => t.value === type)?.label || type;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 size={32} className="animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Website Projects</h1>
          <p className="text-gray-600">Manage your website development portfolio</p>
        </div>
        <button
          onClick={openCreateModal}
          className="flex items-center gap-2 px-4 py-2 bg-cyan-500 text-white font-bold rounded hover:bg-cyan-600 transition-colors"
        >
          <Plus size={18} />
          Add Project
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-cyan-500">
          <p className="text-gray-600 text-sm">Total Projects</p>
          <p className="text-2xl font-bold text-gray-800">{projects.length}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-yellow-500">
          <p className="text-gray-600 text-sm">Featured</p>
          <p className="text-2xl font-bold text-gray-800">{projects.filter(p => p.is_featured).length}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-green-500">
          <p className="text-gray-600 text-sm">Visible</p>
          <p className="text-2xl font-bold text-gray-800">{projects.filter(p => p.is_visible).length}</p>
        </div>
      </div>

      {/* Projects List */}
      {projects.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Globe size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-bold text-gray-600 mb-2">No Projects Yet</h3>
          <p className="text-gray-500 mb-4">Start adding your website development projects to showcase on the Website Services page.</p>
          <button
            onClick={openCreateModal}
            className="px-6 py-2 bg-cyan-500 text-white font-bold rounded hover:bg-cyan-600 transition-colors"
          >
            Add First Project
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project) => (
            <div key={project.id} className="bg-white rounded-lg shadow overflow-hidden">
              {/* Screenshot */}
              <div className="h-40 bg-gradient-to-br from-cyan-500/20 to-purple-500/20 relative group">
                {project.screenshot_url ? (
                  <img 
                    src={project.screenshot_url} 
                    alt={project.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <Globe size={48} className="text-gray-300" />
                  </div>
                )}
                
                {/* Upload overlay */}
                <label className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files[0]) {
                        handleScreenshotUpload(project.id, e.target.files[0]);
                      }
                    }}
                  />
                  <div className="text-white text-center">
                    <Upload size={24} className="mx-auto mb-1" />
                    <span className="text-sm">Upload Screenshot</span>
                  </div>
                </label>
                
                {/* Badges */}
                <div className="absolute top-2 left-2 flex gap-2">
                  {project.is_featured && (
                    <span className="px-2 py-0.5 bg-yellow-500 text-white text-xs font-bold rounded">Featured</span>
                  )}
                  {!project.is_visible && (
                    <span className="px-2 py-0.5 bg-gray-500 text-white text-xs font-bold rounded">Hidden</span>
                  )}
                </div>
              </div>

              {/* Content */}
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="font-bold text-gray-800">{project.title}</h3>
                    <p className="text-sm text-gray-500">{project.client_name}</p>
                  </div>
                  <span className="px-2 py-0.5 bg-cyan-100 text-cyan-700 text-xs font-bold rounded">
                    {getProjectTypeLabel(project.project_type)}
                  </span>
                </div>
                
                <p className="text-sm text-gray-600 line-clamp-2 mb-3">{project.description}</p>
                
                {project.technologies && (
                  <p className="text-xs text-gray-500 mb-3">
                    <Code size={12} className="inline mr-1" />
                    {project.technologies}
                  </p>
                )}
                
                {project.completion_date && (
                  <p className="text-xs text-gray-500 mb-3">
                    <Calendar size={12} className="inline mr-1" />
                    {project.completion_date}
                  </p>
                )}

                {/* Actions */}
                <div className="flex items-center gap-2 pt-3 border-t">
                  {project.project_url && (
                    <a
                      href={project.project_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-cyan-600 hover:bg-cyan-50 rounded transition-colors"
                      title="Visit Website"
                    >
                      <ExternalLink size={18} />
                    </a>
                  )}
                  <button
                    onClick={() => openEditModal(project)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                    title="Edit"
                  >
                    <Edit size={18} />
                  </button>
                  <button
                    onClick={() => handleDelete(project)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                    title="Delete"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-xl font-bold text-gray-800">
                {editingProject ? "Edit Project" : "Add New Project"}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-4 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Project Title *</label>
                    <input
                      type="text"
                      required
                      value={form.title}
                      onChange={(e) => setForm({ ...form, title: e.target.value })}
                      className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                      placeholder="E.g., Modern E-Commerce Store"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Client Name *</label>
                    <input
                      type="text"
                      required
                      value={form.client_name}
                      onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                      className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                      placeholder="Client or company name"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">Description *</label>
                  <textarea
                    required
                    rows={3}
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                    placeholder="Brief description of the project..."
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Project Type *</label>
                    <select
                      required
                      value={form.project_type}
                      onChange={(e) => setForm({ ...form, project_type: e.target.value })}
                      className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                    >
                      {PROJECT_TYPES.map((type) => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Project URL</label>
                    <input
                      type="url"
                      value={form.project_url}
                      onChange={(e) => setForm({ ...form, project_url: e.target.value })}
                      className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                      placeholder="https://example.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Technologies Used</label>
                    <input
                      type="text"
                      value={form.technologies}
                      onChange={(e) => setForm({ ...form, technologies: e.target.value })}
                      className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                      placeholder="React, Node.js, MongoDB..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Completion Date</label>
                    <input
                      type="date"
                      value={form.completion_date}
                      onChange={(e) => setForm({ ...form, completion_date: e.target.value })}
                      className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                    />
                  </div>
                </div>

                <div className="flex gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.is_featured}
                      onChange={(e) => setForm({ ...form, is_featured: e.target.checked })}
                      className="w-4 h-4 text-cyan-600 rounded"
                    />
                    <span className="text-sm font-medium text-gray-700">Featured Project</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.is_visible}
                      onChange={(e) => setForm({ ...form, is_visible: e.target.checked })}
                      className="w-4 h-4 text-cyan-600 rounded"
                    />
                    <span className="text-sm font-medium text-gray-700">Visible on Website</span>
                  </label>
                </div>
              </div>

              <div className="flex gap-3 mt-6 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 border text-gray-700 font-bold rounded hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-2 bg-cyan-500 text-white font-bold rounded hover:bg-cyan-600 transition-colors disabled:opacity-50"
                >
                  {saving ? "Saving..." : (editingProject ? "Update Project" : "Create Project")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Uploading overlay */}
      {uploading && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl flex items-center gap-3">
            <Loader2 size={24} className="animate-spin text-cyan-500" />
            <span className="font-bold text-gray-700">Uploading screenshot...</span>
          </div>
        </div>
      )}
    </div>
  );
}
